import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X } from 'react-feather';
import { twMerge } from 'tailwind-merge';
import { COLORS } from '../../../utils/colors';
import { FormOptionItem, FormOptionItem2 } from '../../../utils/interfaces/generalInterfaces';
import { AppButton } from '../AppButton/AppButton';
import { Ellipsis } from 'react-css-spinners';

export const DillDropdown = ({
  items = [],
  value,
  inputFieldStyles,
  isCreatable = false,
  isSearchable = true,
  isMulti = false,
  disabled = false,
  closeMenu = true,
  placeholder = 'Select',
  label = '',
  labelTextClassName = '',
  isRequired = false,
  onSelectChange = () => {},
  onMultiSelectChange = () => {},
  onCreateNewItem = () => {},
  multiValue,
  errorText = '',
  valueStyle = '',
  customViewElement = null,
  showSelectedItems = false,
  showMinimalDesign = false,
  numberOfItemsToShow = 2,
  itemWidth,
  isLoading = false,
  containerClassName = '',
  menuAlign = 'right',
  tabToNextFieldOnClose = true,
}: {
  label?: string;
  labelTextClassName?: string;
  isRequired?: boolean;
  items: FormOptionItem2[];
  value: string | null;
  isCreatable?: boolean;
  isSearchable?: boolean;
  closeMenu?: boolean;
  isMulti?: boolean;
  disabled?: boolean;
  placeholder?: string;
  inputFieldStyles?: any;
  errorText?: string;
  valueStyle?: string;
  onSelectChange?: (newValue: FormOptionItem) => void;
  onCreateNewItem?: (newValue: string) => void;
  onMultiSelectChange?: (newValue: FormOptionItem[], oldMultiValue?: string[] | null) => void;
  multiValue?: string[] | null;
  customViewElement?: any;
  showSelectedItems?: boolean;
  showMinimalDesign?: boolean;
  numberOfItemsToShow?: number;
  itemWidth?: number;
  isLoading?: boolean;
  containerClassName?: string;
  menuAlign?: 'left' | 'right';
  tabToNextFieldOnClose?: boolean;
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [selectedValue, setSelectedValue] = useState<FormOptionItem | null>();
  const [selectedMultiValues, setSelectedMultiValues] = useState<FormOptionItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [menuPosition, setMenuPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    openUpward: boolean;
  }>({ left: 0, openUpward: false });

  const searchRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLButtonElement>(null);
  const customViewInputRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showMenu && searchRef.current) {
      searchRef.current.focus();
    }
    if (!showMenu) {
      setSearchValue('');
    }
  }, [showMenu]);

  useEffect(() => {
    if (multiValue && isMulti) {
      setSelectedMultiValues(
        multiValue.reduce((prev: FormOptionItem2[], curr) => {
          const check = items.find((ii) => curr === ii.value);
          if (check) {
            return [...prev, check];
          }
          return prev;
        }, [])
      );
    } else {
      if (isMulti) {
        setSelectedMultiValues([]);
        setSelectedValue(null);
      }
    }
  }, [multiValue]);

  useEffect(() => {
    if (value) {
      const check = items.find(
        (item) => item.value && value && item.value.toLowerCase() === value.toLowerCase()
      );
      setSelectedValue(check);
    } else {
      setSelectedValue(null);
    }
  }, [value, items]);

  useEffect(() => {
    if (!showMenu) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!containerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showMenu]);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const menuWidth = itemWidth ?? containerRef.current.offsetWidth;
    const estimatedMenuHeight = 300;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < estimatedMenuHeight && rect.top > estimatedMenuHeight;
    const left = menuAlign === 'left' ? rect.left : rect.right - menuWidth;
    setMenuPosition(
      openUpward
        ? { bottom: window.innerHeight - rect.top + 8, left, openUpward: true }
        : { top: rect.bottom + 8, left, openUpward: false }
    );
  }, [itemWidth, menuAlign]);

  useEffect(() => {
    if (!showMenu) return;
    calculatePosition();
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);
    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [showMenu, calculatePosition]);

  const handleInputClick = (e: any) => {
    if (disabled) return;
    setShowMenu(!showMenu);
  };

  const removeOption = (option: FormOptionItem) => {
    return selectedMultiValues.filter((o) => o.value !== option.value);
  };

  const onTagRemove = (e: any, option: FormOptionItem) => {
    e.stopPropagation();
    const newValue = removeOption(option);
    setSelectedMultiValues(newValue);
    onMultiSelectChange(newValue, multiValue);
  };

  const getDisplay = () => {
    if (!selectedValue && !showSelectedItems) {
      return <p className="text-slate-400 text-xs">{placeholder}</p>;
    }
    if (isMulti && showSelectedItems) {
      return (
        <div>
          {selectedMultiValues.length > 0 ? (
            <div>
              {showMinimalDesign ? (
                <div className="flex justify-between">
                  <div className="flex overflow-hidden">
                    {selectedMultiValues.slice(0, numberOfItemsToShow).map((option) => (
                      <div
                        key={option.value}
                        className="text-left font-semibold text-xs flex items-center text-gray-600 bg-gray-100 px-2 py-1 border border-gray-300 rounded-sm mr-2 max-w-[200px]">
                        <div className="truncate text-xs">{option.label}</div>
                        <span
                          onClick={(e) => onTagRemove(e, option)}
                          className="flex items-center ml-2 font-semibold">
                          <X size={16} color={COLORS.GREY_500} />
                        </span>
                      </div>
                    ))}
                  </div>
                  {selectedMultiValues.length > numberOfItemsToShow && (
                    <div className="text-left font-semibold text-xs flex items-center text-gray-600 bg-gray-100 px-2 py-1 border border-gray-300 rounded-sm mx-2">
                      {selectedMultiValues.length - numberOfItemsToShow}+
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {selectedMultiValues.map((option) => (
                    <div
                      key={option.value}
                      className="text-left font-semibold text-xs flex items-center justify-center text-gray-600 bg-gray-100 px-2 py-1 border border-gray-300 rounded-sm">
                      {option.label}
                      <span
                        onClick={(e) => onTagRemove(e, option)}
                        className="flex items-center ml-2 font-semibold">
                        <X size={16} color={COLORS.GREY_500} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-400 text-xs">{placeholder}</p>
          )}
        </div>
      );
    }
    return selectedValue?.label || '--';
  };

  const onItemClick = (option: FormOptionItem) => {
    if (isMulti) {
      let newValue;
      if (selectedMultiValues.findIndex((o) => o.value === option.value) >= 0) {
        newValue = removeOption(option);
      } else {
        newValue = [...selectedMultiValues, option];
      }
      setSelectedMultiValues(newValue);
      onMultiSelectChange(newValue, multiValue);
    } else {
      onSelectChange(option);
    }
    if (closeMenu) {
      setShowMenu(!showMenu);
    }
  };

  const isSelected = (option: FormOptionItem) => {
    if (isMulti) {
      return selectedMultiValues.filter((o) => o.value === option.value).length > 0;
    }
    if (!selectedValue) {
      return false;
    }
    return selectedValue.value === option.value;
  };

  const onSearch = (e: any) => {
    setSearchValue(e.target.value);
  };

  const getOptions = useMemo(() => {
    if (!searchValue) {
      if (isSearchable) {
        return [
          ...items.filter((item) => item.value.toLowerCase().trim() === 'all'),
          ...items.filter((item) => item.value.toLowerCase().trim() !== 'all'),
        ];
      } else {
        return items;
      }
    }

    return items.filter((option) =>
      option.label.toLowerCase().trim().includes(searchValue.toLowerCase().trim())
    );
  }, [searchValue, items]);

  const checkIfItemExists = () => {
    if (!searchValue) {
      return items;
    }

    return items.filter(
      (option) => option.label.toLowerCase() === searchValue.toLowerCase().trim()
    );
  };

  const isSearchValueIncluded = () => {
    if (!searchValue) {
      return false;
    }

    const check = items.filter((option) =>
      option.label.toLowerCase().trim().includes(searchValue.toLowerCase().trim())
    );
    if (check.length > 0) {
      return true;
    }
    return false;
  };

  const handleCreateNew = () => {
    const availableOptions = items.filter(
      (option) => option.label.toLowerCase() === searchValue.toLowerCase()
    );
    if (availableOptions.length > 0) {
      setShowMenu(false);
      return;
    }

    onCreateNewItem(searchValue);
    setShowMenu(false);
  };

  const dropdownContent = (
    <div
      ref={menuRef}
      className={`fixed bg-white border border-gray-200 z-[9999] shadow-lg overflow-hidden ${
        menuPosition.openUpward ? 'rounded-t-md' : 'rounded-b-md'
      }`}
      style={{
        top: menuPosition.top,
        bottom: menuPosition.bottom,
        left: menuPosition.left,
        width: itemWidth
          ? itemWidth
          : containerRef.current
          ? containerRef.current.offsetWidth
          : '100%',
      }}>
      {isSearchable && (
        <div className="p-2 bg-gray-100 sticky top-0 border-b border-gray-200">
          <input
            onChange={onSearch}
            value={searchValue}
            ref={searchRef}
            className="w-full box-border px-3 py-2 text-xs border border-gray-200 rounded-md outline-none focus:border-blue-500 transition-colors"
            onKeyDown={(e) => {
              if (e.key === 'Tab' || e.key === 'Escape') {
                e.preventDefault();
                setShowMenu(false);
                if (e.key === 'Tab' && tabToNextFieldOnClose) {
                  const focusable = Array.from(
                    document.querySelectorAll<HTMLElement>(
                      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                    )
                  );
                  const idx = inputRef.current ? focusable.indexOf(inputRef.current) : -1;
                  if (idx !== -1 && idx + 1 < focusable.length) {
                    focusable[idx + 1].focus();
                  }
                } else {
                  inputRef.current?.focus();
                }
              }
            }}
          />
        </div>
      )}
      {searchValue === '' && isCreatable && (
        <div className="text-xs px-1 text-blue-500">Type to add new item</div>
      )}
      <div className="w-full overflow-auto max-h-60 bg-white scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {getOptions.length > 0
          ? getOptions.map((option, i) => (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onItemClick(option);
                }}
                key={option.value}
                className={`px-2 py-1.5 flex items-center justify-between gap-2 cursor-pointer transition-colors duration-150 text-left text-xs ${
                  isSelected(option)
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-black hover:bg-blue-100'
                }`}>
                <div className="flex-1 min-w-0">
                  {option.labelInElement ? option.labelInElement : option.label}
                </div>
                {isSelected(option) && (
                  <span className="shrink-0 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                    <Check size={10} color="white" strokeWidth={3} />
                  </span>
                )}
              </div>
            ))
          : !isLoading && (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">No options</div>
            )}
        {isLoading && (
          <div className="py-2 sticky bottom-0">
            <div className="flex flex-col h-4">
              <div
                className={twMerge('w-fit flex flex-col items-center justify-center rounded-lg')}>
                <Ellipsis color="#3762FB" size={70} />
              </div>
            </div>
          </div>
        )}
      </div>
      {searchValue !== '' && isCreatable && checkIfItemExists().length === 0 && (
        <div onClick={handleCreateNew} className="px-2 py-1 flex justify-between">
          <span className="text-xs">
            <span className="text-xs">{'Add '} </span>
            <span className="text-blue-500">{`"${searchValue}"`}</span>
          </span>
          <AppButton
            text="Add New"
            type="SECONDARY"
            buttonStyles={{ padding: '5px 10px' }}
            onClick={handleCreateNew}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className={twMerge('w-full flex flex-col')} ref={containerRef}>
      {(label || isRequired) && (
        <p
          className={twMerge(
            'text-xs mb-0.5 text-left whitespace-nowrap font-medium',
            labelTextClassName
          )}>
          {isRequired && <span className="text-red-500">*</span>}
          {label}
        </p>
      )}
      <div
        className={twMerge(
          'w-full h-fit border py-1 bg-white rounded-lg text-left relative flex items-center border-gray-200',
          containerClassName,
          errorText ? 'border-solid border-red-500' : showMenu ? 'border-solid border-blue-500' : ''
        )}
        style={{
          ...inputFieldStyles,
          ...(disabled
            ? { backgroundColor: '#E4EAF1', cursor: 'not-allowed' }
            : { cursor: 'pointer' }),
        }}>
        {!customViewElement && (
          <button
            type="button"
            disabled={disabled}
            onClick={handleInputClick}
            onFocus={(e) => {
              if (!showMenu && e.target.matches(':focus-visible')) {
                setShowMenu(true);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Tab' || e.key === 'ArrowDown') {
                e.preventDefault();
                if (!showMenu) setShowMenu(true);
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setShowMenu(false);
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setShowMenu(false);
              }
            }}
            className="w-full h-full px-1 flex items-center justify-between select-none bg-transparent border-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset rounded-lg"
            ref={inputRef}>
            <div
              className={disabled ? `text-left text-xs text-gray-300` : `text-left text-xs w-full`}>
              {getDisplay()}
            </div>
            <div className="flex items-center">
              <div
                className={`flex items-center transition-transform duration-200 ${
                  showMenu ? 'rotate-180' : 'rotate-0'
                }`}>
                <ChevronDown size={16} color={disabled ? '#ccc' : '#CBD5E1'} />
              </div>
            </div>
          </button>
        )}
        {customViewElement && (
          <div
            ref={customViewInputRef}
            className={
              disabled ? `text-left text-xs text-gray-300 w-full` : `text-left text-xs w-full`
            }
            onClick={handleInputClick}>
            {customViewElement}
          </div>
        )}
      </div>
      {errorText && <p className="text-2xs mt-0.5 text-red-500">{errorText}</p>}
      {showMenu && createPortal(dropdownContent, document.body)}
    </div>
  );
};
